import { alpha } from '@mui/material/styles';
import { sectionTitleSx } from '../../styles/typography';
import {
    APP_STYLE_COMPONENT_COLORS as MSI_COMPONENT_COLORS,
    APP_STYLE_PALETTE as MSI_PALETTE,
    MSI_TYPE_SCALE,
    usesAppStyleGrammar,
    resolveMsiSxRadius,
} from '../../styles/msiStyleTokens';

export const posSizingTableTitleSx = sectionTitleSx;

export const posSizingActionButtonSx = {
    borderRadius: (theme) => resolveMsiSxRadius(theme, 7, 1.5),
    border: (theme) => `1px solid ${usesAppStyleGrammar(theme) ? MSI_PALETTE.lineDark : alpha(theme.palette.divider, 0.9)}`,
    color: 'text.secondary',
    backgroundColor: (theme) => (
        usesAppStyleGrammar(theme)
            ? MSI_PALETTE.surface
            : theme.palette.mode === 'dark'
            ? theme.palette.background.paper
            : alpha(theme.palette.background.paper, 0.9)
    ),
    '&:hover': {
        backgroundColor: (theme) => (
            usesAppStyleGrammar(theme)
                ? MSI_COMPONENT_COLORS.secondaryHover
                : theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.08)
                : alpha(theme.palette.action.hover, 0.8)
        ),
    },
};

// Icon-only action for a thin panel header; the tooltip carries the label.
export const compactIconButtonSx = {
    ...posSizingActionButtonSx,
    width: 26,
    height: 26,
    p: 0,
    '& .MuiSvgIcon-root': { fontSize: 16 },
};

// A pressed toggle in a thin panel header.
export const compactIconButtonPressedSx = {
    color: 'primary.main',
    borderColor: 'primary.main',
    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
    '&:hover': {
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.16),
    },
};

// A select sized to sit inside a thin panel header.
export const compactSelectSx = {
    '& .MuiInputBase-root': { height: 28, fontSize: '0.74rem' },
    '& .MuiInputLabel-root': { fontSize: '0.74rem' },
};

// Tighter panel padding for panels that use the thin header.
export const compactPanelPaddingSx = { pt: 1, px: 1.5, pb: 1.5 };

// High-contrast themes retain strong panel and header boundaries while body
// rows use a quieter rule. A half-pixel rule alone still renders as a bright
// physical pixel on HiDPI displays, so the body divider also needs reduced
// opacity to lower its visual weight.
export function isHighContrast(theme) {
    return Boolean(theme.palette.highContrast);
}

export function getRowDividerWidth(theme) {
    return isHighContrast(theme) ? '0.5px' : '1px';
}

export function getRowDividerColor(theme) {
    return isHighContrast(theme)
        ? alpha(theme.palette.divider, 0.24)
        : MSI_COMPONENT_COLORS.rowDivider;
}

export const compactStripedTableSx = {
    border: (theme) => (
        isHighContrast(theme) || usesAppStyleGrammar(theme)
            ? `1px solid ${theme.palette.divider}`
            : 'none'
    ),
    borderRadius: (theme) => (usesAppStyleGrammar(theme) ? '8px' : undefined),
    backgroundColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : undefined),
    '& td, & th': {
        px: 1.25,
        py: 0.72,
        borderBottom: (theme) => `${getRowDividerWidth(theme)} solid ${getRowDividerColor(theme)}`,
        whiteSpace: 'nowrap',
    },
    '& td': {
        fontSize: (theme) => (usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableBody.fontSize : '0.84rem'),
        fontWeight: (theme) => (usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableBody.fontWeight : undefined),
        lineHeight: (theme) => (usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableBody.lineHeight : undefined),
        color: 'text.primary',
    },
    '& th': {
        fontWeight: (theme) => (usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableHeader.fontWeight : 600),
        fontSize: (theme) => (usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableHeader.fontSize : '0.78rem'),
        lineHeight: (theme) => (usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.tableHeader.lineHeight : undefined),
        letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : undefined),
        textTransform: (theme) => (usesAppStyleGrammar(theme) ? 'uppercase' : undefined),
        color: 'text.primary',
        backgroundColor: (theme) => (
            usesAppStyleGrammar(theme)
                ? MSI_COMPONENT_COLORS.tableHeader
                : alpha(theme.palette.background.paper, isHighContrast(theme) ? 1 : 0.95)
        ),
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
    },
    '& tbody tr:nth-of-type(even) td': {
        backgroundColor: (theme) => (
            usesAppStyleGrammar(theme)
                ? MSI_PALETTE.surfaceSubtle
                : (isHighContrast(theme) ? 'transparent' : alpha(theme.palette.action.hover, 0.11))
        ),
    },
    '& tbody tr:last-of-type td': {
        borderBottom: 'none',
    },
};
